import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { stringify, parse } from 'querystringify'
import Cookie from 'js-cookie'

// Actions
import { actions as browseActions } from 'modules/browse/actions'
import { actions as recommendationsActions } from 'modules/recommendations/actions'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Selectors
import { selectTrackIdsFromSearchQuery, selectPurgedSearchString, selectQueryObjectFromSearchString } from 'modules/browse/selectors'
import { recommendationsSelector } from 'modules/recommendations/selectors'

// Types
import { QueryObject, State, SearchConfig, DisplayMode } from 'types'

export const useCreateSearchQuery = (queryObj?: QueryObject, skipSearch?: boolean) => {
  const { search } = useLocation()
  return selectPurgedSearchString(
    `${stringify({
      ...queryObj,
      ...parse(skipSearch ? '' : selectPurgedSearchString(search)),
    })}`,
  )
}

export const useSearchConfig = (queryObj: QueryObject = {}): SearchConfig => {
  const searchQuery = useCreateSearchQuery(queryObj, true)
  return useSelector<State, SearchConfig>((state: State) => state.browse.searchconfig[searchQuery])
}

export const useSearchQuery = (queryObj?: QueryObject, dependency?: string | number, skipRecommendationsAndSearchConfig?: boolean) => {
  const dispatch = useDispatch()
  const state = useSelector<State, State>((s: State) => s)

  const searchQuery = useCreateSearchQuery(queryObj)
  const fetchTracksBySearchQueryRequest = useRequest('FETCH_TRACKS_BY_SEARCH_QUERY', searchQuery)
  let recommendations = recommendationsSelector(state, 'recommendationsSong', 'query')
  let recommendationsRequest = useRequest('FETCH_SEARCH_QUERY_RECOMMENDATIONS', searchQuery)
  const collectionRecommendationsRequest = useRequest('FETCH_COLLECTION_RECOMMENDATIONS', queryObj?.collection)
  const artistRecommendationsRequest = useRequest('FETCH_ARTIST_RECOMMENDATIONS', queryObj?.artist)
  const songRecommendationsRequest = useRequest('FETCH_SONG_RECOMMENDATIONS', queryObj?.song)

  if (queryObj?.collection) {
    recommendations = recommendationsSelector(state, 'recommendationsCollection', queryObj.collection)
    recommendationsRequest = collectionRecommendationsRequest
  }
  if (queryObj?.song) {
    recommendations = recommendationsSelector(state, 'recommendationsSong', String(queryObj.song))
    recommendationsRequest = songRecommendationsRequest
  }
  if (queryObj?.artist) {
    recommendations = recommendationsSelector(state, 'recommendationsArtist', queryObj.artist)
    recommendationsRequest = artistRecommendationsRequest
  }
  const { recommendedArtists, recommendedTrackIds, recommendedCollections } = recommendations

  const disableFetch = queryObj?.owned === 0
  useEffect(() => {
    if (!fetchTracksBySearchQueryRequest.isLoading && !fetchTracksBySearchQueryRequest.hasError && !disableFetch) {
      dispatch(browseActions.resetLastPageLoaded(searchQuery))
      dispatch(browseActions.fetchTracksBySearchQuery(searchQuery))
    }
  }, [searchQuery, dispatch, dependency]) // eslint-disable-line

  // Fetch searchconfig for current query but only on first load or if dedendency change
  const searchQueryWithoutSearch = useCreateSearchQuery(queryObj, true)
  const fetchSearchConfigRequest = useRequest('FETCH_SEARCH_CONFIG', searchQueryWithoutSearch)
  const searchConfigLength = Object.keys(useSearchConfig(queryObj) || {}).length

  useEffect(() => {
    if (
      !searchConfigLength &&
      !skipRecommendationsAndSearchConfig &&
      !fetchSearchConfigRequest.isLoading &&
      !fetchSearchConfigRequest.hasError &&
      !disableFetch
    ) {
      dispatch(browseActions.fetchSearchConfig(searchQueryWithoutSearch))
    }
  }, [searchConfigLength, searchQueryWithoutSearch, dispatch, dependency, skipRecommendationsAndSearchConfig]) // eslint-disable-line

  useEffect(() => {
    if (!skipRecommendationsAndSearchConfig && !recommendationsRequest.isLoading && !recommendationsRequest.hasError && !disableFetch) {
      if (queryObj?.song) {
        dispatch(recommendationsActions.fetchSongRecommendations(String(queryObj.song)))
      } else if (queryObj?.collection) {
        dispatch(recommendationsActions.fetchCollectionRecommendations(queryObj.collection))
      } else if (queryObj?.artist) {
        dispatch(recommendationsActions.fetchArtistRecommendations(queryObj.artist))
      } else {
        dispatch(recommendationsActions.fetchQueryRecommendations(searchQuery))
      }
    }
  }, [searchQuery, dispatch, dependency]) // eslint-disable-line

  const trackIds = selectTrackIdsFromSearchQuery(state, searchQuery).map(id => ({ id }))
  const { count, lastPageLoaded } = state.browse.results[searchQuery] || {}
  const contextKey = JSON.stringify(queryObj)

  return useMemo(() => {
    const loadMore = (page: number) => dispatch(browseActions.fetchTracksBySearchQuery(searchQuery, page))
    return {
      trackIds,
      request: fetchTracksBySearchQueryRequest,
      recommendedArtists,
      recommendedTrackIds,
      recommendedCollections,
      recommendationsRequest,
      totalTrackCount: count,
      lastPageLoaded,
      loadMore,
      contextKey,
    }
  }, [
    trackIds,
    fetchTracksBySearchQueryRequest,
    recommendedArtists,
    recommendedTrackIds,
    recommendedCollections,
    recommendationsRequest,
    count,
    lastPageLoaded,
    dispatch,
    searchQuery,
    contextKey,
  ])
}

export const useCurrentQuery = () => {
  const { search } = useLocation()
  return selectQueryObjectFromSearchString(search)
}

export const useDisplayMode = (): DisplayMode => {
  const displayMode = useSelector<State, DisplayMode>(state => state.browse.displayMode)
  return (Cookie.get('displayMode') as DisplayMode) || displayMode
}
